import { create } from 'zustand'
import type { AlvoPreco, PrecoMode, PrecoResolvido, PrecoRow, RegistrarCaptadorForm } from '@/types'
import { COURSE_CATEGORIES } from '@/lib/constants'
import * as precosApi from '@/api/precos'
import { registrarCaptador as registrarCaptadorApi } from '@/api/captadores'
import { getAllCursos } from '@/api/cursos'

export interface CursoPreco {
  id: number
  nomeReduzido: string
  categoria: string
}

interface EditorTarget {
  id: number
  nome: string
  cpfCnpj: string
  mode: PrecoMode
}

interface PrecosState {
  // Landing
  alvos: AlvoPreco[]
  alvosLoading: boolean
  filtroAlvo: string
  fetchAlvos: () => Promise<void>
  setFiltroAlvo: (filtro: string) => void
  registrarCaptador: (dto: RegistrarCaptadorForm) => Promise<void>

  // Editor — loaded target
  target: EditorTarget | null
  cursos: CursoPreco[]
  resolved: PrecoResolvido[]
  legacyHint: { cursoId: number; valor: number }[] | null
  temCaptadoresComPreco: boolean
  loading: boolean
  resolving: boolean
  saving: boolean

  // Editor — the three input levels (deltas; null = no override at that level)
  todos: number | null
  porCategoria: Record<string, number | null>
  porCurso: Record<number, number | null>

  // Selected state tab (null = "Todos os estados" / national). Each tab has its own overrides.
  uf: string | null

  openEditor: (targetId: number) => Promise<void>
  setUf: (uf: string | null) => Promise<void>
  setTodos: (valor: number | null) => void
  setCategoria: (categoria: string, valor: number | null) => void
  setCurso: (cursoId: number, valor: number | null) => void
  salvar: (manterCaptadoresIguais?: boolean) => Promise<PrecoResolvido[]>
  resetEditor: () => void
}

let resolveTimer: ReturnType<typeof setTimeout> | null = null

/** The complete override set the editor manages — one row per level, null where the input is empty. */
function buildRows(
  state: Pick<PrecosState, 'cursos' | 'todos' | 'porCategoria' | 'porCurso'>
): PrecoRow[] {
  const rows: PrecoRow[] = [{ cursoId: null, categoriaCurso: null, valor: state.todos }]
  for (const categoria of COURSE_CATEGORIES) {
    rows.push({
      cursoId: null,
      categoriaCurso: categoria.value,
      valor: state.porCategoria[categoria.value] ?? null,
    })
  }
  for (const curso of state.cursos) {
    rows.push({ cursoId: curso.id, categoriaCurso: null, valor: state.porCurso[curso.id] ?? null })
  }
  return rows
}

function seedInputs(ownRows: PrecoRow[]) {
  const todos =
    ownRows.find((row) => row.cursoId == null && row.categoriaCurso == null)?.valor ?? null
  const porCategoria: Record<string, number | null> = {}
  const porCurso: Record<number, number | null> = {}
  for (const row of ownRows) {
    if (row.cursoId != null) porCurso[row.cursoId] = row.valor
    else if (row.categoriaCurso != null) porCategoria[row.categoriaCurso] = row.valor
  }
  return { todos, porCategoria, porCurso }
}

export const usePrecosStore = create<PrecosState>((set, get) => {
  function scheduleResolve() {
    if (resolveTimer) clearTimeout(resolveTimer)
    resolveTimer = setTimeout(async () => {
      const { target } = get()
      if (!target) return
      set({ resolving: true })
      try {
        const resolved = await precosApi.resolverPrecos(target.id, buildRows(get()), get().uf)
        set({ resolved })
      } catch {
        // keep the last good matrix on a transient resolve error
      } finally {
        set({ resolving: false })
      }
    }, 400)
  }

  return {
    alvos: [],
    alvosLoading: false,
    filtroAlvo: '',

    target: null,
    cursos: [],
    resolved: [],
    legacyHint: null,
    temCaptadoresComPreco: false,
    loading: false,
    resolving: false,
    saving: false,

    todos: null,
    porCategoria: {},
    porCurso: {},
    uf: null,

    fetchAlvos: async () => {
      set({ alvosLoading: true })
      try {
        const alvos = await precosApi.listarAlvos()
        set({ alvos })
      } finally {
        set({ alvosLoading: false })
      }
    },

    setFiltroAlvo: (filtro) => set({ filtroAlvo: filtro }),

    registrarCaptador: async (dto) => {
      await registrarCaptadorApi(dto)
      await get().fetchAlvos() // the new captador shows up as a pricing target
    },

    openEditor: async (targetId) => {
      set({ loading: true, target: null, resolved: [], uf: null })
      const [editorData, allCursos] = await Promise.all([
        precosApi.getEditorData(targetId, null),
        getAllCursos(),
      ])
      const nomeById = new Map(allCursos.map((curso) => [curso.id, curso.nomeReduzido]))
      const cursos: CursoPreco[] = editorData.resolved.map((resolvido) => ({
        id: resolvido.cursoId,
        nomeReduzido: nomeById.get(resolvido.cursoId) ?? `Curso ${resolvido.cursoId}`,
        categoria: resolvido.categoria,
      }))

      const seeded = seedInputs(editorData.ownRows)

      set({
        target: {
          id: editorData.target.id,
          nome: editorData.target.nome,
          cpfCnpj: editorData.target.cpfCnpj,
          mode: editorData.target.mode,
        },
        cursos,
        resolved: editorData.resolved,
        legacyHint: editorData.legacyHint,
        temCaptadoresComPreco: editorData.temCaptadoresComPreco,
        ...seeded,
        loading: false,
      })
    },

    setUf: async (uf) => {
      const { target } = get()
      if (!target) return
      set({ uf, loading: true })
      // Each state tab has its own persisted overrides + resolved matrix — reload for the new uf.
      const editorData = await precosApi.getEditorData(target.id, uf)
      set({
        resolved: editorData.resolved,
        legacyHint: editorData.legacyHint,
        ...seedInputs(editorData.ownRows),
        loading: false,
      })
    },

    setTodos: (valor) => {
      set({ todos: valor })
      scheduleResolve()
    },

    setCategoria: (categoria, valor) => {
      set({ porCategoria: { ...get().porCategoria, [categoria]: valor } })
      scheduleResolve()
    },

    setCurso: (cursoId, valor) => {
      set({ porCurso: { ...get().porCurso, [cursoId]: valor } })
      scheduleResolve()
    },

    salvar: async (manterCaptadoresIguais) => {
      const { target } = get()
      if (!target) return []
      set({ saving: true })
      try {
        const resolved = await precosApi.salvarPrecos(
          target.id,
          buildRows(get()),
          get().uf,
          manterCaptadoresIguais
        )
        set({ resolved })
        return resolved
      } finally {
        set({ saving: false })
      }
    },

    resetEditor: () => {
      if (resolveTimer) clearTimeout(resolveTimer)
      set({
        target: null,
        cursos: [],
        resolved: [],
        legacyHint: null,
        temCaptadoresComPreco: false,
        todos: null,
        porCategoria: {},
        porCurso: {},
        uf: null,
        loading: false,
        resolving: false,
        saving: false,
      })
    },
  }
})
